import './Carousel.less';

import { useState } from 'react';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import classNames from 'classnames';
import { withUIDQueryResults } from 'govbr.frontend.policy/components';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Input, Message } from 'semantic-ui-react';

import { getCarouselImageURL } from './helpers/Image/Image';

import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
  source: {
    id: 'Source',
    defaultMessage: 'Source',
  },
});

// defineMessages({
//   PleaseChooseContent: {
//     id: 'Please choose an existing content as source for this element',
//     defaultMessage:
//       'Please choose an existing content as source for this element',
//   },
//   moreInfo: {
//     id: 'moreInfo',
//     defaultMessage: 'More info',
//   },
// });

const normalizeImageValue = (imageValue) => {
  if (Array.isArray(imageValue)) {
    return imageValue[0] || null;
  }

  return imageValue || null;
};

const hasImageScales = (item, imageField) => {
  if (!item) return false;

  if (item.image_scales?.[imageField]?.[0]) return true;
  if (item.image_scales?.image?.[0]) return true;
  if (item.image?.scales) return true;

  return false;
};

const getResponsiveImageProps = (imageValue, href) => {
  const image = normalizeImageValue(imageValue);

  if (image && image['@id']) {
    const imageField = image.image_field || 'image';
    if (hasImageScales(image, imageField)) {
      return { item: image, imageField };
    }
  }

  if (href && href['@id']) {
    const imageField = href.image_field || 'image';
    if (hasImageScales(href, imageField)) {
      return { item: href, imageField };
    }
  }

  return null;
};

const CarouselBodyBase = ({
  index,
  onChangeBlock,
  block,
  data,
  dataBlock,
  isEditMode,
  openObjectBrowser,
  layout,
  onSlideMediaLoad,
  device = 'phone',
  overwrite = false,
}) => {
  const intl = useIntl();
  const href = data.href?.[0];
  const imageType = href?.image_field;
  const reviewState = href?.review_state;

  const getFieldValue = (field, root = false) => {
    if (!overwrite) {
      return root ? data[field] : data.href?.[0]?.[field];
    }

    if (data.customized?.[`${field}_${device}`]) {
      return data.customized?.[`${field}_${device}`];
    }

    const devicePriorityMap = {
      phone: ['tablet', 'desktop'],
      tablet: ['desktop'],
      desktop: [],
    };

    const prioritizedDevices = devicePriorityMap[device] || [];

    for (const currentDevice of prioritizedDevices) {
      const value = data.customized?.[`${field}_${currentDevice}`];
      if (value) {
        return value;
      }
    }

    return null;
  };

  const title = getFieldValue('Title');
  const description = getFieldValue('Description');
  const image = getFieldValue('preview_image');
  const imageAlignment = `${getFieldValue('horizontal_alignment', true) || 'center'} ${getFieldValue('vertical_alignment', true) || 'center'}`;
  const useLargeContainer = !!dataBlock?.useLargeContainer;
  const imageSizes = useLargeContainer
    ? '100vw'
    : '(min-width: 992px) 992px, 100vw';

  // Texto discernível para acessibilidade do link nos cards do carrossel
  const linkLabel =
    title ||
    getFieldValue('alt') ||
    href?.title ||
    href?.Title ||
    description ||
    href?.['@id'];

  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    setIsDragging(false);
    setDragStart({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  };

  const handleDragMove = (e) => {
    if (dragStart) {
      const dragDistance = Math.sqrt(
        Math.pow(e.clientX - dragStart.x, 2) +
          Math.pow(e.clientY - dragStart.y, 2),
      );
      if (dragDistance > 1) {
        setIsDragging(true);
      }
    }
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDragStart(null);
  };

  const handleClick = () => {
    openObjectBrowser({
      onSelectItem: (url, document) => {
        dataBlock.columns[index].href = [
          {
            '@id': document['@id'],
            Title: document.Title,
            Description: document.Description,
            title: document.Title,
            image_field: document.image_field,
            review_state: document.review_state,
            alt: document.alt || document.Title,
          },
        ];
        if (!dataBlock.columns[index].customized) {
          dataBlock.columns[index].customized = {};
        }
        dataBlock.columns[index].customized.Title_desktop = document.Title;
        dataBlock.columns[index].customized.Description_desktop =
          document.Description;
        dataBlock.columns[index].customized.alt_desktop =
          document.alt || document.Title;

        onChangeBlock(block, dataBlock);
      },
      mode: 'link',
    });
  };

  const handleLinkClick = (e) => {
    if (isDragging) {
      e.preventDefault();
    }
  };

  if (!href && !data.withoutSource && isEditMode) {
    return (
      <Message className="flex-fill">
        <div className="grid-teaser-item default">
          <Image src={imageBlockSVG} alt="" />
          <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
          <div className="toolbar-inner">
            <Button.Group>
              <Button onClick={handleClick} icon basic>
                <Icon name={navTreeSVG} size="24px" />
              </Button>
            </Button.Group>
            <Input
              placeholder={`${intl.formatMessage(messages.source)}...`}
              onClick={handleClick}
              onFocus={(e) => e.target.blur()}
            />
          </div>
        </div>
      </Message>
    );
  }

  const cardClasses = classNames('carousel-block__card', {
    'carousel-block__card--back': layout === 'back',
    'carousel-block__card--with-source': !data.withoutSource,
  });

  if (data.withoutSource) {
    const classes = cardClasses;
    const titleClasses = classNames({
      [`review-state-${reviewState}`]: reviewState,
    });
    const alignStyles = {
      '--carousel-image-align': imageAlignment,
    };

    return (
      <MaybeWrap
        condition={isEditMode}
        className={classes}
        style={alignStyles}
        as="div"
      >
        <MaybeWrap
          condition={!isEditMode}
          as="div"
          tabIndex={-1}
          className={classes}
          style={alignStyles}
          onClick={handleLinkClick}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={(e) => {
            handleDragEnd();
            handleLinkClick(e);
          }}
        >
          <>
            {(image ||
              imageType === 'image' ||
              imageType === 'preview_image') && (
              <div className="carousel-block__image-container">
                {(() => {
                  const responsiveProps = getResponsiveImageProps(image);

                  return (
                    <Image
                      {...(responsiveProps
                        ? { ...responsiveProps, sizes: imageSizes }
                        : {
                            src: flattenToAppURL(
                              getCarouselImageURL(undefined, image, overwrite),
                            ),
                          })}
                      loading="lazy"
                      onLoad={onSlideMediaLoad}
                      alt={getFieldValue('alt') || title}
                    />
                  );
                })()}
              </div>
            )}
            {!data.hide_info && (title || description) && (
              <div className="carousel-block__info">
                {title && <h2 className={titleClasses}>{title}</h2>}
                {description && <p>{description}</p>}
              </div>
            )}
          </>
        </MaybeWrap>
      </MaybeWrap>
    );
  }

  if (href) {
    const classes = cardClasses;
    const titleClasses = classNames({
      [`review-state-${reviewState}`]: reviewState,
    });
    const alignStyles = {
      '--carousel-image-align': imageAlignment,
    };

    return (
      <MaybeWrap
        condition={isEditMode}
        className={classes}
        style={alignStyles}
        as="div"
      >
        <MaybeWrap
          condition={!isEditMode}
          as={UniversalLink}
          href={href['@id']}
          target={data.openLinkInNewTab ? '_blank' : null}
          tabIndex={-1}
          title={linkLabel || undefined}
          aria-label={linkLabel || undefined}
          className={classes}
          style={alignStyles}
          onClick={handleLinkClick}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={(e) => {
            handleDragEnd();
            handleLinkClick(e);
          }}
        >
          <>
            {(href.hasPreviewImage ||
              image ||
              imageType === 'image' ||
              imageType === 'preview_image') && (
              <div className="carousel-block__image-container">
                {(() => {
                  const responsiveProps = getResponsiveImageProps(image, href);

                  return (
                    <Image
                      {...(responsiveProps
                        ? { ...responsiveProps, sizes: imageSizes }
                        : {
                            src: flattenToAppURL(
                              getCarouselImageURL(href, image, overwrite),
                            ),
                          })}
                      loading="lazy"
                      onLoad={onSlideMediaLoad}
                      alt={getFieldValue('alt') || title}
                    />
                  );
                })()}
              </div>
            )}
            {!data.hide_info && (title || description) && (
              <div className="carousel-block__info">
                {title && <h2 className={titleClasses}>{title}</h2>}
                {description && <p>{description}</p>}
              </div>
            )}
          </>
        </MaybeWrap>
      </MaybeWrap>
    );
  }

  return null;
};

const CarouselBody = withUIDQueryResults(CarouselBodyBase, 'href', [
  'alt',
  'image_field',
]);

export default CarouselBody;
